import React from "react";
import { useNavigate } from "react-router-dom";
import { GLOBAL_IMAGES } from "../assets/asset";

const sectionClass = "bg-white/70 rounded-xl p-4 md:p-5 shadow-sm";
const headingClass = "text-[#0B2B66] font-bold text-xl md:text-2xl mb-2";
const textClass = "text-gray-800 text-sm md:text-base leading-relaxed";

const sections = [
  {
    id: 1,
    title: "OUR SERVICES",
    body:
      "The information provided when using the Services is not intended for distribution where prohibited by law. Users accessing from other locations are responsible for compliance with local laws.",
  },
  {
    id: 2,
    title: "INTELLECTUAL PROPERTY RIGHTS",
    body:
      "We are the owner or licensee of all intellectual property rights in the Services, including software, content, graphics, and trademarks. Content is provided AS IS for personal non-commercial use only.",
  },
  {
    id: 3,
    title: "USER REPRESENTATIONS",
    body:
      "By using the Services, you represent that your registration details are accurate, you have legal capacity, and your use is lawful and compliant.",
  },
  {
    id: 4,
    title: "USER REGISTRATION",
    body:
      "You may be required to register and keep your password confidential. You are responsible for all activity under your account.",
  },
  { id: 5, title: "PRODUCTS", body: "Products and prices are subject to availability and may change." },
  {
    id: 6,
    title: "PURCHASES AND PAYMENT",
    body:
      "We accept Apple App Store payments and other approved methods. You agree to provide complete billing details. Payments are in AUD.",
  },
  {
    id: 7,
    title: "SUBSCRIPTIONS",
    body:
      "Subscriptions auto-renew unless cancelled. A 7-day free trial may apply for eligible users. Fee changes may occur with notice.",
  },
  {
    id: 8,
    title: "REFUNDS POLICY",
    body:
      "All sales are final unless refund is available under platform policies and applicable law.",
  },
  {
    id: 9,
    title: "PROHIBITED ACTIVITIES",
    body:
      "You must not misuse the Services, including fraud, harassment, scraping, reverse engineering, malware upload, or unauthorized automation.",
  },
  {
    id: 10,
    title: "USER GENERATED CONTRIBUTIONS",
    body:
      "Where contribution features are available, you are responsible for your content and warrant all required rights and permissions.",
  },
  {
    id: 11,
    title: "CONTRIBUTION LICENCE",
    body:
      "We may process and use submissions and feedback in accordance with the Privacy Policy and these terms.",
  },
  {
    id: 12,
    title: "GUIDELINES FOR REVIEWS",
    body:
      "Reviews must be lawful and non-abusive. We may accept, reject, or remove reviews at our discretion.",
  },
  {
    id: 13,
    title: "MOBILE APPLICATION LICENCE",
    body:
      "Use of the App is limited, revocable, and subject to app-store platform terms.",
  },
  {
    id: 14,
    title: "SERVICES MANAGEMENT",
    body:
      "We may monitor, restrict, suspend, or remove access/content to protect the Services and enforce these terms.",
  },
  {
    id: 15,
    title: "PRIVACY POLICY",
    body:
      "By using the Services, you agree to our Privacy Policy and to data handling described there, including storage and processing in Australia.",
  },
  {
    id: 16,
    title: "TERM AND TERMINATION",
    body:
      "These terms remain in effect while you use the Services. We may suspend or terminate access for breaches or legal reasons.",
  },
  {
    id: 17,
    title: "MODIFICATIONS AND INTERRUPTIONS",
    body:
      "We may update, suspend, or discontinue Services at any time. Uptime and uninterrupted access are not guaranteed.",
  },
  { id: 18, title: "GOVERNING LAW", body: "These Legal Terms are governed by the laws of Australia." },
  {
    id: 19,
    title: "DISPUTE RESOLUTION",
    body:
      "Parties agree to informal negotiations first, then arbitration/court process as defined in these Legal Terms.",
  },
  { id: 20, title: "CORRECTIONS", body: "We may correct errors, inaccuracies, or omissions at any time." },
  {
    id: 21,
    title: "DISCLAIMER",
    body:
      "Services are provided AS-IS and AS-AVAILABLE to the fullest extent permitted by law.",
  },
  {
    id: 22,
    title: "LIMITATIONS OF LIABILITY",
    body:
      "To the fullest extent permitted by law, we are not liable for indirect, incidental, or consequential damages.",
  },
  {
    id: 23,
    title: "INDEMNIFICATION",
    body:
      "You agree to indemnify us against claims and losses arising from your use of Services or breach of these terms.",
  },
  {
    id: 24,
    title: "USER DATA",
    body:
      "We maintain data necessary for Services performance. You are responsible for data you submit and related activity.",
  },
  {
    id: 25,
    title: "ELECTRONIC COMMUNICATIONS, TRANSACTIONS, AND SIGNATURES",
    body:
      "You consent to electronic records, notices, signatures, and communications.",
  },
  {
    id: 26,
    title: "MISCELLANEOUS",
    body:
      "These Legal Terms are the full agreement. If one provision is unenforceable, remaining provisions continue. The app is a tool and does not guarantee improvement in mathematics skills.",
  },
  {
    id: 27,
    title: "CONTACT US",
    body:
      "Byte Digital Consulting Pty Ltd., Level 30/35 Collins St, Melbourne VIC 3000, Australia. Email: mathdingo.support@byte-digital.com.au. Phone: +61-452515004",
  },
];

const SettingsTermsPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen font-mali overflow-y-auto px-4 pt-2 pb-4 md:pt-4 md:pb-6"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg w-full max-w-6xl mx-auto px-4 pt-4 pb-6 sm:px-6 md:px-8 relative">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="absolute top-3 right-3 text-xs md:text-sm font-bold px-3 py-1.5 rounded-md border border-[#0B2B66] text-[#0B2B66] bg-white/90 hover:bg-white"
        >
          Back
        </button>

        <h1 className="text-3xl md:text-4xl font-bold text-[#0B2B66] mb-1">
          Terms and Conditions - MathDingo App, Australia
        </h1>
        <p className="text-sm md:text-base text-gray-700 mb-6">
          Last updated October 27, 2024
        </p>

        <section className={sectionClass}>
          <h2 className={headingClass}>AGREEMENT TO OUR LEGAL TERMS</h2>
          <p className={textClass}>
            We are Byte Digital Consulting Pty Ltd., doing business as Math
            Dingo ("Company", "we", "us", or "our"), a company registered in
            Melbourne, Australia with ABN: 60 656 938 493.
          </p>
          <p className={`${textClass} mt-2`}>
            We operate an interactive iPad application - Math Dingo (the
            "App"), as well as any related products/services linked to these
            Legal Terms. Contact: mathdingo.support@byte-digital.com.au, +61-452515004.
          </p>
          <p className={`${textClass} mt-2`}>
            By accessing Services, you agree to these Legal Terms. If you do
            not agree, discontinue use immediately.
          </p>
        </section>

        <section className={`${sectionClass} mt-4`}>
          <h2 className={headingClass}>TABLE OF CONTENTS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm md:text-base text-[#0B2B66]">
            {sections.map((item) => (
              <a
                key={item.id}
                href={`#section-${item.id}`}
                className="underline hover:text-blue-800"
              >
                {item.id}. {item.title}
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4 mt-4">
          {sections.map((item) => (
            <section key={item.id} id={`section-${item.id}`} className={sectionClass}>
              <h2 className={headingClass}>
                {item.id}. {item.title}
              </h2>
              <p className={textClass}>{item.body}</p>
            </section>
          ))}
        </div>

      </div>
    </div>
  );
};

export default SettingsTermsPolicyPage;
